import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import {
  ArrowLeft,
  Download,
  Check,
  BookOpen,
  CheckCircle2
} from 'lucide-react'
import { MarkdownPreview, Sidebar, ConfirmModal, NicolAIAvatar } from '@/shared/components'
import { ScopingAIPanel } from '../components'
import { cn } from '@/shared/lib/utils'
import { supabase } from '@/shared/lib/supabase'
import type { Project, Team } from '@/shared/types'

interface ProjectScopingPageProps {
  project: Project
  team: Team
}

type StepStatus = 'completed' | 'current' | 'upcoming'

interface HorizontalStepProps {
  number: number
  label: string
  status: StepStatus
  isLast?: boolean
}

function HorizontalStep({ number, label, status, isLast }: HorizontalStepProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        {status === 'completed' ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3.5 w-3.5 text-white" />
          </div>
        ) : status === 'current' ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent">
            <span className="text-xs font-semibold text-white">{number}</span>
          </div>
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-border bg-background">
            <span className="text-xs font-medium text-foreground-muted">{number}</span>
          </div>
        )}
        <span
          className={cn(
            'text-sm',
            status === 'completed' && 'text-foreground-muted',
            status === 'current' && 'font-medium text-foreground',
            status === 'upcoming' && 'text-foreground-muted'
          )}
        >
          {label}
        </span>
      </div>
      {!isLast && (
        <div
          className={cn(
            'mx-2 h-0.5 w-10',
            status === 'completed' ? 'bg-green-500' : 'bg-border'
          )}
        />
      )}
    </>
  )
}

export function ProjectScopingPage({ project, team }: ProjectScopingPageProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()

  // Local state for specs content (can be updated by AI)
  const [specsContent, setSpecsContent] = useState(project.specs_content || '')
  const [specsUpdatedAt, setSpecsUpdatedAt] = useState(project.specs_updated_at)

  // Ref to track the latest specs content for synchronous access in rapid updates
  // This fixes the race condition when multiple specs_updates are applied in quick succession
  const specsContentRef = useRef(specsContent)

  // Sync local state when project changes (navigation between projects)
  useEffect(() => {
    setSpecsContent(project.specs_content || '')
    specsContentRef.current = project.specs_content || ''
    setSpecsUpdatedAt(project.specs_updated_at)
  }, [project.id, project.specs_content, project.specs_updated_at])

  // State for complete scoping modal
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)

  const handleDownloadPdf = () => {
    if (project.specs_pdf_url) {
      window.open(project.specs_pdf_url, '_blank')
    }
  }

  // Merge table content intelligently (keyed by first column: F1, F2, etc.)
  const mergeTableContent = useCallback((existingContent: string, newContent: string): string => {
    // Extract table structure from markdown
    const extractTable = (content: string): {
      headers: string | null
      separator: string | null
      rows: Map<string, string>  // key (F1, F2...) -> full row
      rowOrder: string[]  // preserve order
    } => {
      const lines = content.split('\n')
      let headers: string | null = null
      let separator: string | null = null
      const rows = new Map<string, string>()
      const rowOrder: string[] = []

      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
          if (!headers) {
            headers = trimmed
          } else if (trimmed.match(/^\|[\s\-:|]+\|$/)) {
            separator = trimmed
          } else if (separator) {
            // Extract key from first column (e.g., "F1", "F2", "Risque 1", etc.)
            const keyMatch = trimmed.match(/^\|\s*\*?\*?([^|*]+?)\*?\*?\s*\|/)
            if (keyMatch) {
              const key = keyMatch[1].trim().toLowerCase()
              // Only add if not already present (avoid duplicates)
              if (!rows.has(key)) {
                rows.set(key, trimmed)
                rowOrder.push(key)
              }
            }
          }
        }
      }

      return { headers, separator, rows, rowOrder }
    }

    const existing = extractTable(existingContent)
    const newTable = extractTable(newContent)

    // If both have tables, merge by key
    if (existing.headers && existing.separator && newTable.headers) {
      // Create merged rows: start with existing, update/add from new
      const mergedRows = new Map(existing.rows)
      const mergedOrder = [...existing.rowOrder]

      for (const [key, row] of newTable.rows) {
        if (mergedRows.has(key)) {
          // Update existing row
          mergedRows.set(key, row)
        } else {
          // Add new row
          mergedRows.set(key, row)
          mergedOrder.push(key)
        }
      }

      // Sort order by feature number if possible (F1, F2, F3...)
      mergedOrder.sort((a, b) => {
        const numA = parseInt(a.replace(/\D/g, '')) || 999
        const numB = parseInt(b.replace(/\D/g, '')) || 999
        return numA - numB
      })

      // Rebuild table
      const tableRows = mergedOrder.map(key => mergedRows.get(key)!).join('\n')
      const mergedTable = `${existing.headers}\n${existing.separator}\n${tableRows}\n`

      // Replace table in existing content
      const tableRegex = /\|[^\n]+\|\n\|[\s\-:|]+\|\n(?:\|[^\n]+\|\n?)*/
      if (existingContent.match(tableRegex)) {
        return existingContent.replace(tableRegex, mergedTable)
      }

      return mergedTable
    }

    // No table merge possible - check if new content has subsections
    const hasSubsections = newContent.match(/^###\s+/m)

    if (hasSubsections) {
      // Merge subsections: for each ### in new content, update or append
      const existingSubsections = new Map<string, string>()
      const subsectionRegex = /###\s+([^\n]+)\n([\s\S]*?)(?=###\s+|$)/g
      let match

      // Parse existing subsections
      while ((match = subsectionRegex.exec(existingContent)) !== null) {
        existingSubsections.set(match[1].trim().toLowerCase(), match[0])
      }

      // Parse new subsections and merge
      let mergedContent = existingContent
      subsectionRegex.lastIndex = 0

      while ((match = subsectionRegex.exec(newContent)) !== null) {
        const subsectionTitle = match[1].trim()
        const subsectionKey = subsectionTitle.toLowerCase()
        const newSubsectionContent = match[0]

        if (existingSubsections.has(subsectionKey)) {
          // Replace existing subsection
          const oldSubsection = existingSubsections.get(subsectionKey)!
          mergedContent = mergedContent.replace(oldSubsection, newSubsectionContent)
        } else {
          // Append new subsection
          mergedContent = mergedContent.trim() + '\n\n' + newSubsectionContent
        }
      }

      return mergedContent
    }

    // Fallback: append new content with separator
    return existingContent.trim() + '\n\n' + newContent
  }, [])

  // Handle specs update from AI assistant
  // Uses ref to avoid race conditions when multiple updates are applied rapidly
  const handleSpecsUpdate = useCallback(
    async (update: { section: string; content: string }) => {
      try {
        let updatedContent: string

        // If section is "full", replace entire specs content (initial draft)
        if (update.section === 'full') {
          updatedContent = update.content
        } else {
          // Map section names to document structure (NicolAI v3 - 9 sections)
          const sectionTitles: Record<string, string> = {
            summary: t('scoping.sections.summary'),
            context: t('scoping.sections.context'),
            users: t('scoping.sections.users'),
            features: t('scoping.sections.features'),
            workflows: t('scoping.sections.workflows'),
            architecture: t('scoping.sections.architecture'),
            constraints: t('scoping.sections.constraints'),
            risks: t('scoping.sections.risks'),
            success_criteria: t('scoping.sections.successCriteria'),
          }

          const sectionTitle = sectionTitles[update.section] || update.section

          // Use ref to get the LATEST content (fixes race condition with multiple rapid updates)
          updatedContent = specsContentRef.current

          // Check if section already exists
          const sectionRegex = new RegExp(
            `## ${sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?(?=\\n## |$)`,
            'i'
          )
          const sectionMatch = updatedContent.match(sectionRegex)

          if (sectionMatch) {
            // Section exists - merge content intelligently instead of replacing
            const existingSectionContent = sectionMatch[0]
            const existingBody = existingSectionContent.replace(/^##[^\n]+\n+/, '').trim()

            // Merge the content (handles tables, subsections, etc.)
            const mergedBody = mergeTableContent(existingBody, update.content)

            updatedContent = updatedContent.replace(
              sectionRegex,
              `## ${sectionTitle}\n\n${mergedBody}\n\n`
            )
          } else {
            // Append new section
            updatedContent = updatedContent.trim()
            if (updatedContent) {
              updatedContent += '\n\n'
            }
            updatedContent += `## ${sectionTitle}\n\n${update.content}\n`
          }
        }

        // Update ref SYNCHRONOUSLY so next rapid call sees the latest value
        specsContentRef.current = updatedContent

        // Update React state for UI
        setSpecsContent(updatedContent)
        setSpecsUpdatedAt(new Date().toISOString())

        // Persist to database
        const { error } = await supabase
          .from('projects')
          .update({
            specs_content: updatedContent,
            specs_updated_at: new Date().toISOString(),
          })
          .eq('id', project.id)

        if (error) {
          console.error('Error updating specs:', error)
          toast.error(t('scoping.ai.specsUpdateError'))
        } else {
          // Only show toast for partial updates, not initial draft
          if (update.section !== 'full') {
            toast.success(t('scoping.ai.specsUpdated'))
          }
        }
      } catch (err) {
        console.error('Error updating specs:', err)
        toast.error(t('scoping.ai.specsUpdateError'))
      }
    },
    [project.id, t, mergeTableContent]
  )

  // Handle completing the scoping phase
  const handleCompleteScoping = useCallback(async () => {
    setIsCompleting(true)
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          status: 'quoting',
          scoping_completed_at: new Date().toISOString(),
        })
        .eq('id', project.id)

      if (error) {
        console.error('Error completing scoping:', error)
        toast.error(t('scoping.completeError'))
        return
      }

      toast.success(t('scoping.completeSuccess'))
      setIsCompleteModalOpen(false)

      // Invalidate project cache to refresh data
      await queryClient.invalidateQueries({ queryKey: ['project', 'short', project.short_id] })
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] })

      // Navigate to the quoting page
      navigate(`/p/${project.short_id}`)
    } catch (err) {
      console.error('Error completing scoping:', err)
      toast.error(t('scoping.completeError'))
    } finally {
      setIsCompleting(false)
    }
  }, [project.id, project.short_id, navigate, t, queryClient])

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) {
      return t('scoping.updatedJustNow')
    } else if (diffHours < 24) {
      return t('scoping.updatedHoursAgo', { count: diffHours })
    } else {
      return t('scoping.updatedDaysAgo', { count: diffDays })
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />

      {/* Main content - Specs panel */}
      <main className="flex flex-1 flex-col overflow-hidden bg-background">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-8 py-5">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-background-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Title group */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {team.name}
              </span>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-foreground">
                  {project.name}
                </h1>
                <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700">
                  {t('team.status.scoping')}
                </span>
              </div>
            </div>
          </div>

          {/* Right side: Actions */}
          <div className="flex items-center gap-3">
            {project.specs_pdf_url && (
              <button
                onClick={handleDownloadPdf}
                className="flex h-10 items-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
              >
                <Download className="h-4 w-4" />
                {t('scoping.downloadPdf')}
              </button>
            )}

            {/* Complete Scoping Button - only show if specs exist (during scoping phase) */}
            {specsContent && (
              <button
                onClick={() => setIsCompleteModalOpen(true)}
                className="flex h-10 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
              >
                <CheckCircle2 className="h-4 w-4" />
                {t('scoping.completeButton')}
              </button>
            )}
          </div>
        </div>

        {/* Horizontal Stepper */}
        <div className="flex shrink-0 items-center border-b border-border px-8 py-4">
          <HorizontalStep
            number={1}
            label={t('scoping.step1Short')}
            status="completed"
          />
          <HorizontalStep
            number={2}
            label={t('scoping.step2Short')}
            status="current"
          />
          <HorizontalStep
            number={3}
            label={t('scoping.step3Short')}
            status="upcoming"
          />
          <HorizontalStep
            number={4}
            label={t('scoping.step4Short')}
            status="upcoming"
            isLast
          />
        </div>

        {/* Specs Content - Full height */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Specs Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-border px-8 py-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-[18px] w-[18px] text-foreground" />
              <span className="text-[15px] font-semibold text-foreground">
                {t('scoping.specsTitle')}
              </span>
            </div>
            {specsUpdatedAt && (
              <span className="text-xs text-foreground-muted">
                {formatRelativeTime(specsUpdatedAt)}
              </span>
            )}
          </div>

          {/* Specs Content - Copy protected */}
          <div
            className="flex-1 select-none overflow-auto px-8 py-6"
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {specsContent ? (
              <MarkdownPreview content={specsContent} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
                  <BookOpen className="h-8 w-8 text-foreground-muted" />
                </div>
                <h3 className="mt-4 text-sm font-medium text-foreground">
                  {t('scoping.noSpecsYet')}
                </h3>
                <p className="mt-1 max-w-sm text-sm text-foreground-muted">
                  {t('scoping.noSpecsDescription')}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Right Panel - AI Assistant (NicolAI) - Full height like precadrage */}
      <div className="flex h-screen w-[380px] shrink-0 flex-col border-l border-border bg-slate-50">
        {/* AI Assistant Header */}
        <div className="flex h-14 shrink-0 items-center border-b border-border px-5">
          <div className="flex items-center gap-2.5">
            <NicolAIAvatar size="md" />
            <span className="text-sm font-semibold text-foreground">
              {t('scoping.ai.title')}
            </span>
            <div className="h-2 w-2 rounded-full bg-green-500" />
          </div>
        </div>

        {/* AI Panel */}
        <div className="flex-1 overflow-hidden">
          <ScopingAIPanel
            project={project}
            onSpecsUpdate={handleSpecsUpdate}
          />
        </div>
      </div>

      {/* Complete Scoping Confirmation Modal */}
      <ConfirmModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        onConfirm={handleCompleteScoping}
        isLoading={isCompleting}
        title={t('scoping.completeModalTitle')}
        description={t('scoping.completeModalDescription')}
        confirmLabel={t('scoping.completeModalConfirm')}
      />
    </div>
  )
}
